/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import animate, { easeInOut } from 'components/UI_kit_3/animations/animate';
import ModalHeader, { IModalHeaderProps } from './Modal.Header/Modal.Header';
import ModalBody from './Modal.Body/Modal.Body';
import ModalContext from './modal-context';
import ModalFooter from './Modal.Footer/Modal.Footer';
import styles from './Modal.module.scss';

export interface IModalProps {
  name?: string;
  show: boolean;
  dialogClassName?: string;
  disableBackdropClose?: boolean;
  size?: 'sm' | 'md'
  onHide?: (value?: boolean) => void
  onExit?: () => void
  onExited?: () => void
  onEnter?: () => void
  onEntered?: () => void
}

export type ModalType = React.FC<IModalProps> & {
  Header: React.FC<IModalHeaderProps>;
  Body: React.FC<React.PropsWithChildren<React.HTMLProps<HTMLDivElement>>>;
  Footer: React.FC<React.PropsWithChildren<React.HTMLProps<HTMLDivElement>>>;
};

const Modal: ModalType = ({
  name,
  show,
  dialogClassName,
  disableBackdropClose,
  children,
  size = 'md',
  onHide,
  onExit,
  onExited,
  onEnter,
  onEntered,
}) => {
  const contextValue = React.useMemo(() => ({ onHide }), [onHide]);
  const modalRef = React.useRef<HTMLDivElement>(null);

  const [opacity, setOpacity] = useState<number | ''>(show ? 1 : 0);
  const [mounted, setMounted] = useState<boolean>(!!show);
  const isInitialMount = useRef(true);

  const handleBackdropClick = React.useCallback((e: MouseEvent) => {
    if (e.target !== modalRef.current) return;
    if (onHide && !disableBackdropClose) onHide(false);
  }, [onHide, disableBackdropClose]);

  React.useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.removeProperty('overflow');
    }
    const modalElem = modalRef.current;
    modalElem?.addEventListener('click', handleBackdropClick);
    return () => {
      document.body.style.removeProperty('overflow');
      modalElem?.removeEventListener('click', handleBackdropClick);
    };
  }, [show]);

  React.useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;

      if (show) {
        onEnter?.();
        onEntered?.();
      }
      return;
    }
    if (show) {
      setMounted(true);
      setOpacity(0);
      onEnter?.();
      animate({
        duration: 100,
        timing: easeInOut,
        draw: (progress) => {
          setOpacity(progress);
        },
        end: () => {
          onEntered?.();
        },
      });
    } else {
      if (onExit) {
        onExit();
      }
      animate({
        duration: 100,
        timing: easeInOut,
        draw: (progress) => {
          setOpacity(1 - progress);
        },
        end: () => {
          onExited?.();
          setMounted(false);
        },
      });
    }
  }, [show]);

  if (!mounted) return null;
  return createPortal(
    (
      <div
        ref={modalRef}
        className={classNames(styles.modal)}
        data-overlay-ignore="true"
        style={{
          opacity: opacity === 1 ? undefined : opacity,
          visibility: opacity === 0 ? 'hidden' : undefined,
        }}
      >
        <ModalContext.Provider value={contextValue}>
          <div
            className={classNames(dialogClassName, styles.modalDialog, styles[size])}
          >
            {children}
          </div>
        </ModalContext.Provider>
      </div>
    ),
    document.body,
  );
};

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
